import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { anzahlBewertungen, bewertungenSchnitt, hatEchteBewertungen } from "../data/bewertungen";

/**
 * Zählt beim Hereinscrollen von 0 auf `to` hoch.
 *
 * Bewusst mit IntersectionObserver und requestAnimationFrame statt mit
 * useInView/animate aus framer-motion: unter React.StrictMode wird jeder
 * Effekt doppelt ausgeführt, und dabei kam die jeweils erste Kachel des
 * Rasters nicht in Gang - sie blieb dauerhaft auf 0 stehen.
 */
const DAUER_MS = 2200;

function Counter({ to, decimals = 0, prefix = "", suffix = "" }) {
  const ref = useRef(null);
  const [val, setVal] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    let frame = 0;

    const zaehle = () => {
      const start = performance.now();
      const schritt = (jetzt) => {
        const t = Math.min((jetzt - start) / DAUER_MS, 1);
        setVal(to * (1 - Math.pow(1 - t, 4))); // schneller Start, weiches Auslaufen
        if (t < 1) frame = requestAnimationFrame(schritt);
      };
      frame = requestAnimationFrame(schritt);
    };

    const beobachter = new IntersectionObserver(
      (eintraege) => {
        if (!eintraege.some((e) => e.isIntersecting)) return;
        beobachter.disconnect();
        zaehle();
      },
      { rootMargin: "-15% 0px" },
    );
    beobachter.observe(el);

    return () => {
      beobachter.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [to]);

  return (
    <span ref={ref}>
      {prefix}
      {val.toFixed(decimals).replace(".", ",")}
      {suffix}
    </span>
  );
}

// Die Durchschnittsnote erscheint erst, wenn echte Google-Bewertungen
// vorliegen (siehe src/data/bewertungen.js). Ohne sie wäre sie eine
// Behauptung ohne Grundlage - dann stehen hier nur zwei Kacheln.
const stats = [
  { to: 100, suffix: " %", label: "Zufriedene Kunden" },
  ...(hatEchteBewertungen
    ? [
        {
          to: bewertungenSchnitt,
          decimals: 1,
          label: "Ø Kundenbewertung",
          sub: `aus ${anzahlBewertungen} Google-Bewertungen`,
        },
      ]
    : []),
  {
    to: 2,
    label: "Feste Ansprechpartner",
    sub: "Sie sprechen immer mit denselben zwei Personen",
  },
];

export default function Stats() {
  return (
    <section id="warum" data-testid="stats-section" className="relative z-10 px-6 py-28 md:px-14 md:py-40">
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-15%" }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="mb-20 grid gap-10 md:grid-cols-2"
        >
          <div>
            <p className="mb-6 text-[11px] tracking-[0.45em] text-[#D4AF37] md:text-sm">WARUM WIR · WEBDESIGN-AGENTUR AUGSBURG</p>
            <h2 className="font-display text-4xl font-extrabold uppercase leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl">
              Warum Betriebe aus Augsburg mit uns{" "}
              <em className="gold-text font-serif-accent normal-case italic">arbeiten.</em>
            </h2>
          </div>
          <p className="max-w-md self-end text-base font-light leading-relaxed text-[#BDBDB6] md:text-lg">
            Wir sind kein anonymer Dienstleister. Wir sind Ihre Webdesign-Agentur in Augsburg —
            persönlich erreichbar, kompromisslos in der Qualität und erst zufrieden, wenn Sie
            es sind.
          </p>
        </motion.div>

        <div
          className={`grid gap-12 border-t border-white/5 pt-16 ${
            stats.length === 3 ? "sm:grid-cols-3" : "sm:grid-cols-2"
          }`}
        >
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              data-testid={`stat-${i}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ delay: i * 0.15, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="gold-text font-display text-6xl font-extrabold md:text-7xl">
                <Counter to={s.to} decimals={s.decimals || 0} suffix={s.suffix || ""} />
              </div>
              <div className="mt-4 h-px w-16 bg-gradient-to-r from-[#D4AF37] to-transparent" />
              <p className="mt-4 text-base font-medium tracking-wide text-[#F5F5F0]">{s.label}</p>
              {s.sub && <p className="mt-1 text-sm font-light text-[#BDBDB6]">{s.sub}</p>}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
